import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Download, Loader2, RefreshCw, Send, X,
} from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { BillReportScreen } from '../components/bills/BillReportScreen';
import { SendBillReportDialog } from '../components/bills/SendBillReportDialog';
import { BulkSendReportsDialog } from '../components/bills/BulkSendReportsDialog';
import { BulkStatusSelect } from '../components/bills/BulkStatusSelect';
import { WorkflowStepPicker } from '../components/bills/WorkflowStepPicker';
import { ReportBatchesPanel } from '../components/reports/ReportBatchesPanel';
import { BillFilterBar } from '../components/common/BillFilterBar';
import { billService } from '../services/billService';
import { bulkReportService } from '../services/bulkReportService';
import type { BvBill } from '../types/billTypes';
import {
  reportFigures, reportBucket, reportSendStatus, canSend, BUCKET_LABELS, SEND_STATUS_LABELS, NO_SENDS,
  type ReportBucket, type ReportFigures, type ReportSendStatus, type SendHistory,
  networkIncomplete,
} from '../lib/reportStatus';
import { WORKFLOW_LABELS } from '../lib/billWorkflow';
import {
  BUCKET_VERDICT, VERDICT_LABELS, billFilterOptions, matchesBillFilters, type BillFilterContext,
} from '../lib/billFilters';
import { useRowSelection } from '../utils/useRowSelection';
import { useBillVerdicts } from '../hooks/useBillVerdicts';
import { useBillFilters } from '../hooks/useBillFilters';
import { useBoardRows } from '../hooks/useBoardRows';
import { useWorkflowStep } from '../hooks/useWorkflowStep';
import type { BillVerdict } from '../lib/billVerdict';
import type { BillStatus } from '../utils/billStatus';

// Bulk Customer Report — every bill that has a report, what each report says
// (overcharge, undercharge, within range), whether it has gone to the client,
// and bulk send. Both are derived by lib/reportStatus from the bill and its
// bv_report_send history.

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU');
}

function fmtDateTime(d: string) {
  return new Date(d).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

// Same palette as the report's own verdict (BillReportScreen): red over, orange under.
const BUCKET_BADGE: Record<ReportBucket, string> = {
  overcharge: 'bg-red-100 text-red-700',
  undercharge: 'bg-orange-100 text-orange-700',
  within_range: 'bg-emerald-100 text-emerald-700',
};
const BUCKET_TEXT: Record<ReportBucket, string> = {
  overcharge: 'text-red-600',
  undercharge: 'text-orange-600',
  within_range: 'text-emerald-600',
};

const SEND_BADGE: Record<ReportSendStatus, string> = {
  no_report:          'bg-gray-50 text-gray-400 border-gray-200',
  not_sent:           'bg-blue-50 text-blue-700 border-blue-200',
  send_failed:        'bg-red-50 text-red-700 border-red-300',
  sent:               'bg-slate-100 text-slate-700 border-slate-200',
  changed_since_sent: 'bg-amber-50 text-amber-800 border-amber-300',
};

type CardDef<K extends string> = { key: K; label: string; hint: string; tone: string };

const RESULT_CARDS: CardDef<ReportBucket>[] = [
  { key: 'overcharge', label: 'Overcharge', hint: 'Retailer charged more than expected', tone: BUCKET_TEXT.overcharge },
  { key: 'undercharge', label: 'Undercharge', hint: 'Retailer charged less than expected', tone: BUCKET_TEXT.undercharge },
  { key: 'within_range', label: 'Within range', hint: 'Within 5% of the expected invoice', tone: BUCKET_TEXT.within_range },
];
const SEND_CARDS: CardDef<ReportSendStatus>[] = [
  { key: 'not_sent', label: 'Not sent', hint: 'Select and send', tone: 'text-blue-600' },
  { key: 'sent', label: 'Sent', hint: 'Delivered to the client', tone: 'text-slate-700' },
  { key: 'send_failed', label: 'Send failed', hint: 'See the error on each report; retry', tone: 'text-red-600' },
  { key: 'changed_since_sent', label: 'Changed since sent', hint: 'Figures moved after sending', tone: 'text-amber-600' },
];

// The verdicts a report can have (its Result cards); the rest are pre-report states.
const REPORT_VERDICTS: BillStatus[] = ['overcharge', 'undercharge', 'matched'];

type RunFilter = { runId: string; startedAt: string | null; billIds: Set<string> };

type Row = {
  bill: BvBill;
  figures: ReportFigures;
  /** The client report's own totals; null only before a verdict can be formed. */
  verdict: BillVerdict | null;
  bucket: ReportBucket;
  status: ReportSendStatus;
  history: SendHistory;
  sendable: boolean;
};

type StatusCardProps = {
  testKey: string;
  label: string;
  hint: string;
  tone: string;
  count: number;
  active: boolean;
  onClick: () => void;
};

const StatusCard = ({ testKey, label, hint, tone, count, active, onClick }: StatusCardProps) => (
  <button
    type="button"
    data-testid={`reports-card-${testKey}`}
    data-active={active}
    onClick={onClick}
    className={cn(
      'text-left rounded-xl border bg-white px-4 py-3 shadow-sm transition-colors hover:bg-gray-50',
      active ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200',
    )}
  >
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <p data-testid={`reports-count-${testKey}`} className={cn('text-2xl font-bold mt-0.5', tone)}>{count}</p>
    <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>
  </button>
);

export function BillValidationReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const runParam = searchParams.get('run');

  const [bills, setBills] = useState<BvBill[]>([]);
  const verdictFor = useBillVerdicts(bills);
  const [history, setHistory] = useState<Record<string, SendHistory>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [runFilter, setRunFilter] = useState<RunFilter | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  // Search, months, site fields, verdict, status and send status: in the URL,
  // shared with Bills and Bulk Bill Run (hooks/useBillFilters). The Result and
  // Send status cards set `verdict` and `send`.
  const { filters, query, setFilters, clearFilters } = useBillFilters();
  // Each bill's status (lib/billWorkflow) from the board's rows: the Status
  // filter, the row's status and Set status. Secondary, as on the Bills list.
  const board = useBoardRows(verdictFor);
  const { saving: savingStep, error: stepError, setStep } = useWorkflowStep(board.reload);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [sendingRow, setSendingRow] = useState<Row | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [batchesKey, setBatchesKey] = useState(0);
  const [sendAgainError, setSendAgainError] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const selection = useRowSelection();

  const loadHistory = useCallback(async () => {
    const res = await bulkReportService.sendHistoryByBill();
    if (res.success) { setHistory(res.data ?? {}); setHistoryError(null); }
    else setHistoryError(res.error ?? 'Could not load send status');
  }, []);
  // A send changes a bill's workflow status too (Sent to Customer): reload the
  // board's rows with the send history so the row label and Status filter agree.
  const reloadBoard = board.reload;
  const refreshSendState = useCallback(() => { void loadHistory(); reloadBoard(); }, [loadHistory, reloadBoard]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [billRes, sendRes] = await Promise.all([billService.listAll(), bulkReportService.sendHistoryByBill()]);
        if (!mounted) return;
        if (billRes.success) setBills(billRes.data ?? []);
        else setError(billRes.error ?? 'Failed to load');
        // Without send status every report would read as not sent — say so
        // rather than invite a second send of something already sent.
        if (sendRes.success) setHistory(sendRes.data ?? {});
        else setHistoryError(sendRes.error ?? 'Could not load send status');
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!runParam) { setRunFilter(null); setRunError(null); return; }
    let mounted = true;
    void bulkReportService.runReportBillIds(runParam).then((res) => {
      if (!mounted) return;
      if (res.success && res.data) {
        setRunFilter({ runId: runParam, startedAt: res.data.startedAt, billIds: new Set(res.data.billIds) });
        setRunError(null);
      } else {
        setRunFilter(null);
        setRunError(res.error ?? 'Could not load that bill run');
      }
    });
    return () => { mounted = false; };
  }, [runParam]);

  // Only bills that HAVE a report. The rest are still in the pipeline — that is
  // Bulk Bill Run's view, linked from the line under the cards.
  const rows: Row[] = useMemo(() => bills.flatMap((b) => {
    const figures = reportFigures(b);
    // Network charges not priced: an incomplete calculation, not a report.
    if (!figures || networkIncomplete(b)) return [];
    const h = history[b.id] ?? NO_SENDS;
    const verdict = verdictFor(b);
    return [{
      bill: b, figures, verdict, bucket: reportBucket(figures, verdict),
      status: reportSendStatus(b, h, verdict), history: h, sendable: canSend(b, h, verdict),
    }];
  }), [bills, history, verdictFor]);
  const notReadyCount = bills.length - rows.length;

  // A report row as the shared filters read it (lib/billFilters). Its verdict
  // is the report's own bucket, so the Result cards and the filter cannot disagree.
  const ctxOf = useCallback((r: Row): BillFilterContext => ({
    verdict: BUCKET_VERDICT[r.bucket],
    workflow: board.rowsById.get(r.bill.id)?.status ?? null,
    sendStatus: r.status,
  }), [board.rowsById]);
  const options = useMemo(() => billFilterOptions(rows.map((r) => r.bill)), [rows]);

  // Every filter but the two the cards set narrows the scope first, so the
  // Result and Send status cards count what clicking them would show.
  const inScope = useMemo(() => rows.filter((r) =>
    (!runFilter || runFilter.billIds.has(r.bill.id))
    && matchesBillFilters(r.bill, ctxOf(r), { ...filters, verdict: '', send: '' })),
  [rows, runFilter, ctxOf, filters]);

  const bucketCounts = inScope.reduce<Record<ReportBucket, number>>(
    (acc, { bucket }) => { acc[bucket]++; return acc; },
    { overcharge: 0, undercharge: 0, within_range: 0 },
  );
  const sendCounts = inScope.reduce<Record<ReportSendStatus, number>>(
    (acc, { status }) => { acc[status]++; return acc; },
    { no_report: 0, not_sent: 0, send_failed: 0, sent: 0, changed_since_sent: 0 },
  );

  const filtered = useMemo(
    () => inScope.filter((r) => matchesBillFilters(r.bill, ctxOf(r), filters)),
    [inScope, ctxOf, filters],
  );

  // Any report can be selected (Set status); only sendable ones go out with
  // Send selected — a report already with the client is re-sent deliberately
  // from its own Send to client. A selected row hidden by a filter drops out:
  // what is acted on is what is on screen.
  const sendableIds = filtered.filter((r) => r.sendable).map((r) => r.bill.id);
  const selectedRows = filtered.filter((r) => selection.isSelected(r.bill.id));
  const selectedBills = selectedRows.filter((r) => r.sendable).map((r) => r.bill);

  const toggleExpand = (bill: BvBill) => setExpandedId((prev) => (prev === bill.id ? null : bill.id));

  const clearRunFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('run');
    setSearchParams(next);
  };

  const handleDownloadPdf = async (bill: BvBill, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!reportRef.current) return;
    setDownloadingId(bill.id);
    try {
      const { downloadReportAsPdf } = await import('../utils/pdfUtils');
      const target = (reportRef.current.querySelector('[data-pdf-target]') ?? reportRef.current) as HTMLElement;
      await downloadReportAsPdf(target, `BillValidation_${bill.site?.nmi ?? bill.id}_${fmtDate(bill.bill_from)}.pdf`);
    } catch {
      // silent
    } finally {
      setDownloadingId(null);
    }
  };

  // The card a verdict filter came from, so its chip reads like the card.
  const verdictBucket = (Object.keys(BUCKET_VERDICT) as ReportBucket[])
    .find((b) => BUCKET_VERDICT[b] === filters.verdict) ?? null;
  const activeChips = [
    filters.verdict && {
      key: 'bucket',
      label: verdictBucket ? BUCKET_LABELS[verdictBucket] : VERDICT_LABELS[filters.verdict],
      clear: () => setFilters({ verdict: '' }),
    },
    filters.send && { key: 'send', label: SEND_STATUS_LABELS[filters.send], clear: () => setFilters({ send: '' }) },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  return (
    <div data-testid="reports-page" className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Customer Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every customer report by result — overcharge, undercharge or within range — and send them together.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshSendState} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh send status
        </Button>
      </div>

      {runParam && (
        <div data-testid="reports-run-filter" className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            {runFilter
              ? `Reports from the bill run of ${runFilter.startedAt ? fmtDateTime(runFilter.startedAt) : 'this run'} (${runFilter.billIds.size})`
              : runError ?? 'Loading bill run…'}
            <button onClick={clearRunFilter} aria-label="Show all reports" className="hover:text-blue-900">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      {historyError && (
        <p data-testid="reports-history-error" className="flex gap-2 items-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Send status could not be loaded ({historyError}) — sent and failed reports may show as not sent. Refresh send status before sending.
        </p>
      )}

      <ReportBatchesPanel
        refreshKey={batchesKey}
        onProgress={refreshSendState}
        onSendAgain={(billId) => {
          const row = rows.find((r) => r.bill.id === billId);
          if (row) { setSendAgainError(null); setSendingRow(row); }
          // Can happen if the bill's report was deleted, or the bill no longer
          // qualifies for a report (e.g. network charges un-priced again)
          // since the batch ran — nothing left here to reopen the dialog on.
          else setSendAgainError("That bill's report is no longer available on this page.");
        }}
      />
      {sendAgainError && (
        <p data-testid="reports-send-again-error" className="text-xs text-red-700">{sendAgainError}</p>
      )}

      {/* Result | send status */}
      <div className="grid gap-4 xl:grid-cols-[3fr_4fr]">
        <section>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Result</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {RESULT_CARDS.map((def) => (
              <StatusCard
                key={def.key}
                testKey={def.key}
                label={def.label}
                hint={def.hint}
                tone={def.tone}
                count={bucketCounts[def.key]}
                active={filters.verdict === BUCKET_VERDICT[def.key]}
                onClick={() => setFilters({
                  verdict: filters.verdict === BUCKET_VERDICT[def.key] ? '' : BUCKET_VERDICT[def.key],
                })}
              />
            ))}
          </div>
        </section>
        <section>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Send status</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SEND_CARDS.map((def) => (
              <StatusCard
                key={def.key}
                testKey={def.key}
                label={def.label}
                hint={def.hint}
                tone={def.tone}
                count={sendCounts[def.key]}
                active={filters.send === def.key}
                onClick={() => setFilters({ send: filters.send === def.key ? '' : def.key })}
              />
            ))}
          </div>
        </section>
      </div>

      <p data-testid="reports-scope-line" className="text-xs text-gray-500">
        <span data-testid="reports-count-total" className="font-semibold text-gray-700">{inScope.length}</span> customer
        report{inScope.length === 1 ? '' : 's'}.
        {notReadyCount > 0 && !runFilter && (
          <> <span data-testid="reports-not-ready">{notReadyCount}</span> bill{notReadyCount === 1 ? ' isn’t' : 's aren’t'} ready
            for a report yet (not priced, network charges not priced, or no retailer invoice) —{' '}
            <Link to={`/bill-validation/bill-runs${query ? `?${query}` : ''}`} className="text-blue-600 hover:underline">see Bulk Bill Run</Link>.</>
        )}
      </p>

      {/* Filters: the ones every bill list shares, then a clear chip for each
          card filter that is set. */}
      <BillFilterBar
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        options={options}
        testIdPrefix="reports"
        searchPlaceholder="Search client or NMI…"
        verdictOptions={REPORT_VERDICTS}
      >
        {activeChips.map((chip) => (
          <button
            key={chip.key}
            data-testid={`reports-filter-clear-${chip.key}`}
            onClick={chip.clear}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
          >
            {chip.label} <X className="h-3 w-3" />
          </button>
        ))}
      </BillFilterBar>

      {/* Bulk toolbar */}
      {!loading && !error && filtered.length > 0 && (
        <div data-testid="reports-bulk-toolbar" className="flex items-center gap-3 flex-wrap rounded-lg border border-gray-200 bg-white px-4 py-2.5">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              data-testid="reports-select-all"
              type="checkbox"
              disabled={sendableIds.length === 0}
              checked={selection.allSelected(sendableIds)}
              ref={(el) => { if (el) el.indeterminate = !selection.allSelected(sendableIds) && selection.someSelected(sendableIds); }}
              onChange={() => selection.toggleAll(sendableIds)}
            />
            Select all not yet sent ({sendableIds.length})
          </label>
          <span className="text-xs text-gray-400">
            Reports not sent yet, and reports whose send failed (retry). Any report can be selected to set its status.
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selectedRows.length > 0 && <Button variant="ghost" size="sm" onClick={selection.clear}>Clear</Button>}
            {selectedRows.length > 0 && (
              <BulkStatusSelect
                testId="reports-action-status"
                disabled={savingStep}
                onSet={(step) => void setStep(selectedRows.map((r) => r.bill.id), step)}
              />
            )}
            <Button
              data-testid="reports-send-selected"
              size="sm"
              disabled={selectedBills.length === 0}
              onClick={() => setBulkOpen(true)}
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            >
              <Send className="h-3.5 w-3.5" /> Send selected ({selectedBills.length})
            </Button>
          </div>
        </div>
      )}
      {stepError && (
        <p data-testid="reports-step-error" className="text-xs text-red-700">{stepError}</p>
      )}

      {/* List */}
      {loading ? (
        <div data-testid="reports-loading" className="text-center py-16 text-gray-400">Loading reports…</div>
      ) : error ? (
        <div data-testid="reports-error" className="text-center py-16 text-red-500">{error}</div>
      ) : filtered.length === 0 ? (
        <div data-testid="reports-empty" className="text-center py-16 text-gray-400">
          {inScope.length === 0 ? 'No customer reports yet.' : 'No reports match the current filter.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const { bill, figures, verdict, bucket, status, history: h, sendable } = row;
            // The figures the report itself states (compared charges and their
            // GST), so the row never disagrees with the report it expands to.
            // The raw totals still carry unmapped lines on the invoice side.
            const shadowTotal = verdict?.compared.expected ?? figures.shadowResult.total;
            const actualTotal = verdict?.compared.invoice ?? figures.actualResult.total;
            const diff = verdict?.variance ?? actualTotal - shadowTotal;
            const isExpanded = expandedId === bill.id;
            const isDownloading = downloadingId === bill.id;
            const lastFailed = h.lastAttempt?.status === 'failed' ? h.lastAttempt : null;

            return (
              <div
                key={bill.id}
                data-testid="reports-row"
                data-bill-id={bill.id}
                data-bucket={bucket}
                data-send-status={status}
                data-expanded={isExpanded}
                className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              >
                <div
                  data-testid="reports-row-toggle"
                  onClick={() => toggleExpand(bill)}
                  className="px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <input
                      data-testid="reports-row-select"
                      type="checkbox"
                      className="shrink-0"
                      title={sendable
                        ? 'Select to send or to set its status'
                        : 'Select to set its status — already sent, so Send selected skips it (use Send to client to send it again)'}
                      checked={selection.isSelected(bill.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => selection.toggle(bill.id)}
                    />
                    <span className="text-gray-400 shrink-0">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </span>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* The bill's own page (full comparison, invoice, history). Stops the
                            click so following the link does not also expand the row. */}
                        <Link
                          data-testid="reports-row-bill-link"
                          to={`/bill-validation/bills/${bill.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-semibold text-gray-900 hover:text-blue-600 hover:underline"
                          title="Open this bill"
                        >
                          {bill.site?.client?.name ?? '—'}
                        </Link>
                        <span className="text-gray-300">·</span>
                        {bill.site_id ? (
                          <Link
                            data-testid="reports-row-site-link"
                            to={`/bill-validation/sites/${bill.site_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-mono text-gray-500 hover:text-blue-600 hover:underline"
                            title="Open this site"
                          >
                            {bill.site?.nmi ?? '—'}
                          </Link>
                        ) : (
                          <p className="text-sm font-mono text-gray-500">{bill.site?.nmi ?? '—'}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap text-xs text-gray-500">
                        <span>{fmtDate(bill.bill_from)} – {fmtDate(bill.bill_to)}{bill.billing_days ? ` · ${bill.billing_days} days` : ''}</span>
                        {h.lastSent && (
                          <span data-testid="reports-row-sent-at" className="inline-flex items-center gap-1 text-slate-600">
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Sent {fmtDateTime(h.lastSent.at)}
                          </span>
                        )}
                      </div>
                      {lastFailed && (
                        <p data-testid="reports-row-send-error" className="mt-1 flex items-start gap-1 text-xs text-red-600 break-words">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                          Send failed {fmtDateTime(lastFailed.at)}{lastFailed.error ? ` — ${lastFailed.error}` : ''}
                        </p>
                      )}
                    </div>

                    <div className="text-right hidden md:block min-w-[110px]">
                      <p className="text-xs text-gray-400 mb-0.5">Expected</p>
                      <p className="text-sm font-medium text-gray-700">{fmtCurrency(shadowTotal)}</p>
                    </div>
                    <div className="text-right hidden md:block min-w-[110px]">
                      <p className="text-xs text-gray-400 mb-0.5">Actual</p>
                      <p className="text-sm font-medium text-gray-700">{fmtCurrency(actualTotal)}</p>
                    </div>
                    <div className="text-right min-w-[100px]">
                      <p className="text-xs text-gray-400 mb-0.5">Variance</p>
                      <p className={cn('text-sm font-semibold', BUCKET_TEXT[bucket])}>
                        {`${diff >= 0 ? '+' : ''}${fmtCurrency(diff)}`}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      {(() => {
                        const boardRow = board.rowsById.get(bill.id);
                        if (!boardRow) return null;
                        // The row toggles on click; the picker must not.
                        return (
                          <div className="flex flex-col items-end" onClick={(e) => e.stopPropagation()}>
                            <span
                              data-testid="reports-row-workflow"
                              data-status={boardRow.status}
                              className={cn('text-xs font-medium', boardRow.status === 'internal_review' || boardRow.status === 'system_review' ? 'text-amber-700' : 'text-gray-700')}
                            >
                              {WORKFLOW_LABELS[boardRow.status]}
                            </span>
                            <WorkflowStepPicker
                              status={boardRow.status}
                              bucket={boardRow.bucket}
                              step={boardRow.bill.workflow_status ?? null}
                              disabled={savingStep}
                              onChange={(step) => void setStep([bill.id], step)}
                            />
                          </div>
                        );
                      })()}
                      <span data-testid="report-bucket-badge" data-bucket={bucket} className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', BUCKET_BADGE[bucket])}>
                        {BUCKET_LABELS[bucket]}
                      </span>
                      <span data-testid="report-send-badge" data-send-status={status} className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border', SEND_BADGE[status])}>
                        {SEND_STATUS_LABELS[status]}
                      </span>
                      {isExpanded && (
                        <button
                          data-testid="reports-download-pdf"
                          onClick={(e) => void handleDownloadPdf(bill, e)}
                          disabled={isDownloading}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
                        >
                          {isDownloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          {isDownloading ? 'Generating…' : 'Download PDF'}
                        </button>
                      )}
                      {isExpanded && (
                        <button
                          data-testid="reports-send-client"
                          onClick={(e) => { e.stopPropagation(); setSendingRow(row); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                        >
                          <Send className="h-3.5 w-3.5" /> Send to client
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div data-testid="reports-inline-report" className="border-t border-gray-200" ref={reportRef}>
                    <BillReportScreen
                      bill={bill}
                      shadowResult={figures.shadowResult}
                      actualResult={figures.actualResult}
                      gstExcluded={figures.gstExcluded}
                      retailer={bill.site?.current_retailer ?? undefined}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {sendingRow && (
        <SendBillReportDialog
          open
          onClose={() => { setSendingRow(null); refreshSendState(); }}
          bill={sendingRow.bill}
          shadowResult={sendingRow.figures.shadowResult}
          actualResult={sendingRow.figures.actualResult}
          gstExcluded={sendingRow.figures.gstExcluded}
        />
      )}

      {bulkOpen && (
        <BulkSendReportsDialog
          open
          bills={selectedBills}
          onClose={(queuedAny) => {
            setBulkOpen(false);
            // The batch sends in the background; the panel follows it and
            // reloads send status as bills finish. Failures are recorded too.
            selection.clear();
            if (queuedAny) setBatchesKey((k) => k + 1);
            refreshSendState();
          }}
        />
      )}
    </div>
  );
}
